import { AddAPhotoOutlined, HideImageOutlined, NoPhotographyOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import { NeedsToBeMovedToStagingVehicleDetailDto } from 'dtos/toDoLists/outboundTrips'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { NeedsToBeMovedToStagingVehicleDetailValidator } from 'validators/toDoLists/outboundTrips'

interface WarrantyCertPhotoCaptureDialogProps {
  initialValues: NeedsToBeMovedToStagingVehicleDetailDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: NeedsToBeMovedToStagingVehicleDetailDto) => void
  open: boolean
}

export default function WarrantyCertPhotoCaptureDialog({ initialValues, isLoading, onClose, onSave, open }: WarrantyCertPhotoCaptureDialogProps) {
  const [other, setOther] = useState<boolean>(false)
  const [objectURL, setObjectURL] = useState<string | null>(null)

  useEffect(() => {
    if (initialValues.warrantyCertificateInGloveBoxFile) {
      setObjectURL(URL.createObjectURL(initialValues.warrantyCertificateInGloveBoxFile))
    }

    return () => {
      if (objectURL) {
        URL.revokeObjectURL(objectURL)
        setObjectURL(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValues])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => onSave(values)}
        validateOnBlur
        validateOnChange
        validationSchema={NeedsToBeMovedToStagingVehicleDetailValidator}
      >
        {({ errors, handleBlur, handleChange, setFieldValue, touched, values }) => {
          return (
            <Form>
              <DialogContent>
                <Grid container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <DialogTitle>Warranty Cert Photo Capture</DialogTitle>
                  </Grid>

                  {objectURL ? (
                    <Grid item xs={12}>
                      <img alt='Warranty Cert' src={objectURL} style={{ width: '100%' }} />
                    </Grid>
                  ) : (
                    <Grid item xs={12} container alignItems='center' justifyContent='center' sx={{ mb: 1 }}>
                      <Grid item sx={{ backgroundColor: '#989898', borderRadius: 4 }}>
                        <NoPhotographyOutlined sx={{ color: '#C4C5C7', fontSize: '256pt', pr: 1 }} />
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <input
                      accept='image/jpeg,image/jpg,image/png'
                      capture='environment'
                      hidden
                      id='warrantyCertificateInGloveBoxFile'
                      type='file'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setFieldValue('warrantyCertificateInGloveBoxFile', e.target.files[0])
                          if (objectURL) URL.revokeObjectURL(objectURL)
                          setObjectURL(URL.createObjectURL(e.target.files[0]))
                        }
                      }}
                    />

                    <Button
                      color='primary'
                      endIcon={<AddAPhotoOutlined />}
                      fullWidth
                      onClick={() => document.getElementById('warrantyCertificateInGloveBoxFile')?.click()}
                      variant='outlined'
                    >
                      BROWSE OR TAKE PHOTO
                    </Button>

                    {errors.warrantyCertificateInGloveBoxFile && <FormHelperText error>{errors.warrantyCertificateInGloveBoxFile}</FormHelperText>}
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={!values.warrantyCertificateInGloveBoxFile}
                      endIcon={<HideImageOutlined />}
                      fullWidth
                      onClick={() => {
                        setFieldValue('warrantyCertificateInGloveBoxFile', undefined)
                        if (objectURL) URL.revokeObjectURL(objectURL)
                        setObjectURL(null)
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='body1'>
                      Please select the location that the vehicle is being moved to for this Outbound Trip. This will update the "Parking Space" on the vehicle
                      record.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='body1'>Note: If "Other" is selected, the text field becomes enabled and required.</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <RadioGroup
                        name='parkingSpace'
                        onChange={e => {
                          const parkingSpace = e.target.value

                          if (parkingSpace === 'Other') {
                            setOther(true)
                            setFieldValue('parkingSpace', '')
                          } else {
                            setOther(false)
                            setFieldValue('parkingSpace', parkingSpace)
                          }
                        }}
                      >
                        <FormControlLabel value='Staging' control={<Radio checked={values.parkingSpace === 'Staging'} />} label='Staging' />
                        <FormControlLabel value='Truck' control={<Radio checked={values.parkingSpace === 'Truck'} />} label='Truck' />
                        <FormControlLabel value='Other' control={<Radio checked={other} />} label='Other' />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || !other}
                      label='Other'
                      name='parkingSpace'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={other ? values.parkingSpace : ''}
                    />

                    {errors.parkingSpace && <FormHelperText error>{errors.parkingSpace}</FormHelperText>}
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Photo Previewer:**

Displays a preview of the uploaded photo.

If no photo is uploaded, nothing will be displayed.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button opens the user’s camera app, allowing them to:

   - Take a new photo and upload it.
   - Upload an existing photo from their device.

**DELETE UPLOADED PHOTO Button:**

Clicking this button will:

   - Clear/delete the currently uploaded photo.
   - Remove it from the Photo Previewer.

**Parking Space Radio Buttons:**

Displays the parking space options as radio buttons and is required. Users can only select 1 parking space.

If the users has selected the “Other” radio button option the “Other” text field is enabled and required.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not save any changes.

**SAVE Button:**

Clicking this button will:

   - Save the uploaded photo to the database and close the dialog`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
